import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WifiIcon from '@mui/icons-material/Wifi';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';

// Helper to strip HTML tags for clean display
function cleanHtml(htmlOrText) {
  if (!htmlOrText || typeof htmlOrText !== 'string' || !htmlOrText.trim()) return '';
  const clean = htmlOrText
    .replace(/<p><br><\/p>/g, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return clean;
}

export default function MobileDailyDevotionModal({
  open = false,
  onClose,
  isExample = false,
  devotion = null,
}) {
  const [activeSlide, setActiveSlide] = useState(0); // 0 = Devotion, 1 = Quote of the day
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedDay, setSelectedDay] = useState(26);

  // Example devotion data mirroring the user's uploaded mobile screenshots
  const exampleData = {
    series: 'Way Truth Life Church',
    title: 'PHILIP DODDRIDGE - STANDING UP FOR GOD UNCOMPROMISINGLY',
    author: 'Jim Sole',
    dateStr: 'Monday, Oct 26, 2026',
    content: `Life demands that we make adjustments and amends with it, so that we live our life goes smoothly. This is normal and natural.

However, when it comes to the life of a Christian, we are called to live by the standards of the LORD God Almighty, our Creator and our Redeemer.

There is no compromising of the standards of the Bible, nor any adjustments and amends that we live.`,
    quote: `Daniel is a great model for us in so many ways; a man of integrity, conviction, and non-compromise. And the way he developed such a life was through consistent, God-seeking, God-honouring, relational prayer, not in times of emergency, but as a way of life. May we follow his example and develop such a glowing testimony of the God who is real as we seek to represent Him in a world desperately needing to know Him.`,
  };

  const effectiveSeries = isExample || !devotion
    ? exampleData.series
    : (devotion.category || devotion.section_name || devotion.origanisation?.org_name || 'Daily Devotion');

  const effectiveTitle = isExample || !devotion || !devotion.title
    ? exampleData.title
    : devotion.title;

  const effectiveAuthor = isExample || !devotion || !devotion.author
    ? exampleData.author
    : devotion.author;

  const effectiveDate = isExample || !devotion
    ? exampleData.dateStr
    : (devotion.post_date
      ? new Date(devotion.post_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })
      : exampleData.dateStr);

  const effectiveContent = isExample || !devotion || !devotion.post_content
    ? exampleData.content
    : cleanHtml(devotion.post_content);

  const effectiveQuote = isExample || !devotion || !devotion.quote
    ? exampleData.quote
    : cleanHtml(devotion.quote);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '44px',
          bgcolor: '#000000',
          p: 0,
          boxShadow: '0 25px 65px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.08)',
          border: '8px solid #1E293B',
          overflow: 'hidden',
          width: '375px',
          maxWidth: '95vw',
          height: '690px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          m: 'auto',
          position: 'relative',
        },
      }}
    >
      {/* ── TOP STATUS BAR (Android/iOS Mobile Status Bar) ── */}
      <Box
        sx={{
          bgcolor: '#000000',
          px: 2.5,
          pt: 1.2,
          pb: 0.8,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#E2E8F0',
          fontSize: '0.82rem',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#FFFFFF' }}>
          16:59
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#E2E8F0' }}>
          <SignalCellularAltIcon sx={{ fontSize: 14 }} />
          <WifiIcon sx={{ fontSize: 14 }} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800 }}>5G</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700 }}>75</Typography>
            <BatteryFullIcon sx={{ fontSize: 14 }} />
          </Box>
        </Box>
      </Box>

      {/* ── MOBILE APP TOP BAR (< Back | Series Name | Close) ── */}
      <Box
        sx={{
          bgcolor: '#000000',
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: '#FFFFFF',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 800,
            fontSize: '0.95rem',
            color: '#FFFFFF',
            maxWidth: '220px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {effectiveSeries}
        </Typography>

        <Box display="flex" alignItems="center" gap={0.5}>
          <Box
            onClick={() => setCalendarOpen(false)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: '#FFFFFF',
              gap: 0.3,
            }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Back</Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: '#94A3B8',
              ml: 1,
              p: 0.3,
              '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── MAIN SCROLLABLE MOBILE CONTENT (Pure white background like authentic mobile app) ── */}
      <Box
        sx={{
          flex: 1,
          bgcolor: '#FFFFFF',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* Daily Devotion Action Bar (Daily Devotion + Play + Auto + Calendar) */}
        <Box
          sx={{
            px: 2.5,
            pt: 2.5,
            pb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontSize: '1.45rem',
              fontWeight: 900,
              color: '#0F172A',
              letterSpacing: '-0.5px',
            }}
          >
            Daily Devotion
          </Typography>

          <Box display="flex" alignItems="center" gap={1}>
            {/* Play Button */}
            <Box
              onClick={() => setIsPlaying(!isPlaying)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  bgcolor: '#1E6F80',
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(30, 111, 128, 0.4)',
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: '#165563', transform: 'scale(1.05)' }
                }}
              >
                {isPlaying ? <PauseIcon sx={{ fontSize: 20 }} /> : <PlayArrowIcon sx={{ fontSize: 22 }} />}
              </Box>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#1E6F80', mt: 0.3 }}>
                {isPlaying ? 'Pause' : 'Play'}
              </Typography>
            </Box>

            {/* Auto Button */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.4,
                px: 1.2,
                py: 0.6,
                borderRadius: '16px',
                bgcolor: '#E2E8F0',
                color: '#64748B',
                cursor: 'pointer',
              }}
            >
              <ArrowDownwardIcon sx={{ fontSize: 13 }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Auto</Typography>
            </Box>

            {/* Calendar Button */}
            <Box
              onClick={() => setCalendarOpen(!calendarOpen)}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '14px',
                bgcolor: calendarOpen ? '#1E6F80' : '#E0F2FE',
                color: calendarOpen ? '#FFFFFF' : '#0284C7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': { transform: 'scale(1.05)' }
              }}
            >
              <CalendarMonthIcon sx={{ fontSize: 22 }} />
            </Box>
          </Box>
        </Box>

        {/* ── SLIDE CONTENT (Devotion Body vs Quote of the Day) ── */}
        <Box sx={{ flex: 1, px: 2.5, pb: 2, display: 'flex', flexDirection: 'column' }}>
          {activeSlide === 0 ? (
            /* SLIDE 1: Devotion Content */
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography
                sx={{
                  fontSize: '1.18rem',
                  fontWeight: 900,
                  color: '#0F172A',
                  lineHeight: 1.35,
                  mb: 1.2,
                  textTransform: 'uppercase',
                  letterSpacing: '-0.3px',
                }}
              >
                {effectiveTitle}
              </Typography>

              {/* Author */}
              <Box display="flex" alignItems="center" gap={0.6} mb={2.2}>
                <PersonOutlineIcon sx={{ fontSize: 18, color: '#0284C7' }} />
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#0284C7' }}>
                  {effectiveAuthor}
                </Typography>
              </Box>

              {/* Text Paragraphs */}
              <Box sx={{ color: '#1E293B', fontSize: '1.02rem', lineHeight: 1.72, whiteSpace: 'pre-line', mb: 2 }}>
                {effectiveContent}
              </Box>

              {/* Bottom Swipe / Toggle for Quote */}
              <Box
                onClick={() => setActiveSlide(1)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mt: 'auto',
                  pt: 2,
                  pb: 1,
                  cursor: 'pointer',
                }}
              >
                <Box display="flex" gap={0.5}>
                  <Box sx={{ width: 14, height: 5, borderRadius: 3, bgcolor: '#1E6F80' }} />
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#CBD5E1' }} />
                </Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1E6F80' }}>
                  Swipe for Quote &gt;
                </Typography>
              </Box>
            </Box>
          ) : (
            /* SLIDE 2: Quote of the Day */
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', pt: 1 }}>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 900,
                  color: '#1E6F80',
                  letterSpacing: '0.5px',
                  textAlign: 'center',
                  mb: 2,
                }}
              >
                QUOTE OF THE DAY
              </Typography>

              {/* Quote Card */}
              <Box
                sx={{
                  bgcolor: '#FFFFFF',
                  borderRadius: '16px',
                  p: 3,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                  border: '1px solid #E2E8F0',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontStyle: 'italic',
                    fontSize: '0.92rem',
                    lineHeight: 1.65,
                    color: '#1E293B',
                    mb: 2,
                  }}
                >
                  "{effectiveQuote}"
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    color: '#0F172A',
                    textAlign: 'left',
                  }}
                >
                  -- {effectiveAuthor}
                </Typography>
              </Box>

              {/* Bottom Slider Dots: Return to Devotion */}
              <Box
                onClick={() => setActiveSlide(0)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mt: 3,
                  mb: 1,
                  cursor: 'pointer',
                }}
              >
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1E6F80' }}>
                  &lt; Back to Devotion
                </Typography>
                <Box display="flex" gap={0.5}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#CBD5E1' }} />
                  <Box sx={{ width: 14, height: 5, borderRadius: 3, bgcolor: '#1E6F80' }} />
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* ── CALENDAR POPUP OVERLAY (Screenshot 3) ── */}
        {calendarOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 48,
              bgcolor: 'rgba(15, 23, 42, 0.45)',
              backdropFilter: 'blur(3px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
              zIndex: 10,
            }}
            onClick={() => setCalendarOpen(false)}
          >
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                bgcolor: '#FFFFFF',
                borderRadius: '20px',
                p: 2.5,
                width: '100%',
                maxWidth: '310px',
                boxShadow: '0 16px 36px rgba(0,0,0,0.25)',
              }}
            >
              <Typography
                sx={{
                  textAlign: 'center',
                  fontWeight: 800,
                  fontSize: '1rem',
                  color: '#1E6F80',
                  mb: 1,
                }}
              >
                Select Date
              </Typography>

              {/* Legend: Read / Unread */}
              <Box display="flex" justifyContent="center" gap={3} mb={1.5}>
                <Box display="flex" alignItems="center" gap={0.6}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10B981' }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>
                    Read
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.6}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#EF4444' }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>
                    Unread
                  </Typography>
                </Box>
              </Box>

              {/* Month Header */}
              <Box display="flex" justifyContent="space-between" alignItems="center" px={1} mb={1}>
                <ArrowBackIosNewIcon sx={{ fontSize: 13, color: '#0284C7', cursor: 'pointer' }} />
                <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: '#0F172A' }}>
                  October 2026
                </Typography>
                <ArrowForwardIosIcon sx={{ fontSize: 13, color: '#0284C7', cursor: 'pointer' }} />
              </Box>

              {/* Days of week */}
              <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" textAlign="center" mb={1}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <Typography key={d} sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748B' }}>
                    {d}
                  </Typography>
                ))}
              </Box>

              {/* Calendar Grid (Days 1 to 31) */}
              <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0.5} textAlign="center">
                {/* 4 Empty offset days */}
                {[27, 28, 29, 30].map(d => (
                  <Typography key={`prev-${d}`} sx={{ fontSize: '0.78rem', color: '#CBD5E1', py: 0.4 }}>
                    {d}
                  </Typography>
                ))}
                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => {
                  const isSelected = day === selectedDay;
                  const isRead = [16, 20, 22, 26].includes(day);
                  return (
                    <Box
                      key={day}
                      onClick={() => { setSelectedDay(day); setCalendarOpen(false); }}
                      sx={{
                        py: 0.3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        borderRadius: '50%',
                      }}
                    >
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isSelected ? '#1E6F80' : 'transparent',
                          color: isSelected ? '#FFFFFF' : '#0F172A',
                          fontWeight: isSelected ? 800 : 600,
                          fontSize: '0.78rem',
                        }}
                      >
                        {day}
                      </Box>
                      <Box
                        sx={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          bgcolor: isRead ? '#10B981' : '#EF4444',
                          mt: 0.2,
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── BOTTOM DATE NAVIGATION BAR (< Monday, Oct 26, 2026 >) ── */}
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          borderTop: '1px solid #E2E8F0',
          py: 0.9,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <IconButton size="small" sx={{ color: '#0F172A', p: 0.4 }}>
          <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
        </IconButton>

        <Typography sx={{ fontSize: '0.86rem', fontWeight: 800, color: '#0F172A' }}>
          {effectiveDate}
        </Typography>

        <IconButton size="small" sx={{ color: '#0F172A', p: 0.4 }}>
          <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* ── HOME INDICATOR BAR ── */}
      <Box sx={{ bgcolor: '#000000', pt: 0.4, pb: 0.8, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
        <Box sx={{ width: 90, height: 3.5, bgcolor: '#FFFFFF', borderRadius: 2 }} />
      </Box>
    </Dialog>
  );
}
